import React, {memo, useCallback, useState, useMemo, useEffect} from "react"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {ColumnModel, SettingsList} from "../../../../components/Settings/SettingsList"
import {setEntityListAction} from "../../../../store/control/actions"
import {useLocalization} from "../../../../hooks/localization"
import {CallLogList} from "../../../../store/control/state"
import {useDispatch, useSelector} from "react-redux"
import {useRPC} from "../../../../hooks/rpc"
import {useQuestion} from "../../../../hooks/question"

const keyExtractor = (item: CallLogList) => item.id
export const CallLogsScreen = memo(function CallLogsScreen() {
    const [rpc] = useRPC()
    const {t} = useLocalization()
    const dispatch = useDispatch()
    const question = useQuestion()
    const callLogList = useSelector(state => state.control.callLogListList)
    const newList = useMemo(
        () =>
            callLogList.map(r => {
                const dateObject = new Date(Number(r.call_time) * 1000)
                let yearString = dateObject.getFullYear().toString()
                if (yearString.length === 1) {
                    yearString = "0" + yearString
                }
                let monthString = (dateObject.getMonth() + 1).toString()
                if (monthString.length === 1) {
                    monthString = "0" + monthString
                }
                let dayString = dateObject.getDate().toString()
                if (dayString.length === 1) {
                    dayString = "0" + dayString
                }
                let hourString = dateObject.getHours().toString()
                if (hourString.length === 1) {
                    hourString = "0" + hourString
                }
                let minuteString = dateObject.getMinutes().toString()
                if (minuteString.length === 1) {
                    minuteString = "0" + minuteString
                }
                const dateString = `${dayString}/${monthString}/${yearString} ${hourString}:${minuteString}`
                return {
                    id: r?.id,
                    call_status: r?.call_status === "" ? t("call_log_list.unanswered") : t("call_log_list.answered"),
                    door_status: r?.door_status === "" ? t("call_log_list.not_opened") : r?.door_status,
                    caller: r?.caller,
                    call_time: dateString,
                    gateway_name: r?.gateway_name
                }
            }),
        [t, callLogList]
    )
    const [selectedControl, setSelectedControl] = useState<CallLogList>(null)
    const displayNameExtractor = useCallback((item: CallLogList, column: ColumnModel) => {
        if (column.key === "name") {
            return item
        }
        return item[column.key] || "-"
    }, [])

    const columns = useMemo<ColumnModel[]>(
        () => [
            {
                length: 2,
                name: t("time"),
                key: "call_time"
            },
            {
                length: 2,
                name: t("call_log_list.flat"),
                key: "caller"
            },
            {
                length: 2,
                name: "Panel",
                key: "gateway_name"
            },
            {
                length: 2,
                name: t("call_log_list.meeting"),
                key: "call_status"
            },
            {
                length: 2,
                name: t("call_log_list.door_status"),
                key: "door_status"
            }
        ],
        [t]
    )

    const onPressDeleteLog = useCallback(() => {
        question(t("are_you_sure"), () => {
            rpc.deleteCallLogList()
                .then(() => dispatch(setEntityListAction([], "callLogList")))
                .then(() => setSelectedControl(null))
        })
    }, [question, t, dispatch, rpc])

    const buttons = useMemo(
        () => [
            {
                text: t("delete"),
                onClick: onPressDeleteLog,
                className: "red",
                hidden: !selectedControl
            }
        ],
        [t, onPressDeleteLog, selectedControl]
    )

    useEffect(() => {
        rpc.getCallLogList().then(data => dispatch(setEntityListAction(data, "callLogList")))
    }, [rpc, dispatch])

    return (
        <PasswordContainer requiredRole="admin" isFullscreen={true}>
            <SettingsPageContainer
                className="service-edit"
                buttons={buttons}
                backUrl="/settings/logs"
                searchable={true}
            >
                <SettingsList
                    columns={columns}
                    items={newList}
                    keyExtractor={keyExtractor}
                    displayNameExtractor={displayNameExtractor}
                    selectedItem={selectedControl}
                    onItemSelected={setSelectedControl}
                />
            </SettingsPageContainer>
        </PasswordContainer>
    )
})
